// ============================================================================
// BP-09-04: Community Store
// BrightPath Community - Zustand State Management
// ============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  ForumPostType,
  MentorSearchParams,
  SupportGroupSearchParams,
  GroupType,
} from '../types';

// =============================================================================
// Types
// =============================================================================

interface ForumFilters {
  postType?: ForumPostType;
  sortBy: 'recent' | 'popular' | 'most_replies';
  searchQuery: string;
}

interface MentorFilters {
  diagnosisId?: string;
  expertise?: string;
  language?: string;
  isAccepting?: boolean;
}

interface GroupFilters {
  diagnosisId?: string;
  groupType?: GroupType;
  city?: string;
  state?: string;
  hasAvailability?: boolean;
}

interface CommunityState {
  // Forum State
  forumFilters: ForumFilters;
  selectedCategorySlug: string | null;
  
  // Mentor State
  mentorFilters: MentorFilters;
  
  // Support Group State
  groupFilters: GroupFilters;
  
  // UI State
  searchQuery: string;
  activeTab: 'forums' | 'mentors' | 'groups';
  
  // Actions
  setForumFilters: (filters: Partial<ForumFilters>) => void;
  resetForumFilters: () => void;
  setSelectedCategory: (slug: string | null) => void;
  
  setMentorFilters: (filters: Partial<MentorFilters>) => void;
  resetMentorFilters: () => void;
  
  setGroupFilters: (filters: Partial<GroupFilters>) => void;
  resetGroupFilters: () => void;
  
  setSearchQuery: (query: string) => void;
  setActiveTab: (tab: 'forums' | 'mentors' | 'groups') => void;
  
  resetAll: () => void;
}

// =============================================================================
// Initial State
// =============================================================================

const initialForumFilters: ForumFilters = {
  postType: undefined,
  sortBy: 'recent',
  searchQuery: '',
};

const initialMentorFilters: MentorFilters = {
  diagnosisId: undefined,
  expertise: undefined,
  language: undefined,
  isAccepting: undefined,
};

const initialGroupFilters: GroupFilters = {
  diagnosisId: undefined,
  groupType: undefined,
  city: undefined,
  state: undefined,
  hasAvailability: undefined,
};

// =============================================================================
// Store
// =============================================================================

export const useCommunityStore = create<CommunityState>()(
  devtools(
    persist(
      (set) => ({
        // Initial State
        forumFilters: initialForumFilters,
        selectedCategorySlug: null,
        mentorFilters: initialMentorFilters,
        groupFilters: initialGroupFilters,
        searchQuery: '',
        activeTab: 'forums',

        // Forum Actions
        setForumFilters: (filters) =>
          set(
            (state) => ({
              forumFilters: { ...state.forumFilters, ...filters },
            }),
            false,
            'setForumFilters'
          ),

        resetForumFilters: () =>
          set({ forumFilters: initialForumFilters }, false, 'resetForumFilters'),

        setSelectedCategory: (slug) =>
          set({ selectedCategorySlug: slug }, false, 'setSelectedCategory'),

        // Mentor Actions
        setMentorFilters: (filters) =>
          set(
            (state) => ({
              mentorFilters: { ...state.mentorFilters, ...filters },
            }),
            false,
            'setMentorFilters'
          ),

        resetMentorFilters: () =>
          set({ mentorFilters: initialMentorFilters }, false, 'resetMentorFilters'),

        // Group Actions
        setGroupFilters: (filters) =>
          set(
            (state) => ({
              groupFilters: { ...state.groupFilters, ...filters },
            }),
            false,
            'setGroupFilters'
          ),

        resetGroupFilters: () =>
          set({ groupFilters: initialGroupFilters }, false, 'resetGroupFilters'),

        // UI Actions
        setSearchQuery: (query) =>
          set({ searchQuery: query }, false, 'setSearchQuery'),

        setActiveTab: (tab) =>
          set({ activeTab: tab }, false, 'setActiveTab'),

        // Reset All
        resetAll: () =>
          set(
            {
              forumFilters: initialForumFilters,
              selectedCategorySlug: null,
              mentorFilters: initialMentorFilters,
              groupFilters: initialGroupFilters,
              searchQuery: '',
              activeTab: 'forums',
            },
            false,
            'resetAll'
          ),
      }),
      {
        name: 'brightpath-community',
        partialize: (state) => ({
          activeTab: state.activeTab,
          // Don't persist filters to avoid stale state
        }),
      }
    ),
    { name: 'CommunityStore' }
  )
);

// =============================================================================
// Selectors
// =============================================================================

export const selectForumFilters = (state: CommunityState) => state.forumFilters;
export const selectMentorFilters = (state: CommunityState) => state.mentorFilters;
export const selectGroupFilters = (state: CommunityState) => state.groupFilters;
export const selectActiveTab = (state: CommunityState) => state.activeTab;

// =============================================================================
// Helper to convert store filters to API params
// =============================================================================

export function forumFiltersToParams(filters: ForumFilters): Record<string, unknown> {
  return {
    post_type: filters.postType,
    sort_by: filters.sortBy,
    search: filters.searchQuery || undefined,
  };
}

export function mentorFiltersToParams(filters: MentorFilters): MentorSearchParams {
  return {
    diagnosis_id: filters.diagnosisId,
    expertise: filters.expertise,
    language: filters.language,
    is_accepting: filters.isAccepting,
  };
}

export function groupFiltersToParams(filters: GroupFilters): SupportGroupSearchParams {
  return {
    diagnosis_id: filters.diagnosisId,
    group_type: filters.groupType,
    city: filters.city,
    state: filters.state,
    has_availability: filters.hasAvailability,
  };
}
